import { type AriaPopoverProps, DismissButton, Overlay, usePopover } from "@react-aria/overlays";
import { type ReactNode, type RefObject, useRef } from "react";
import type { OverlayTriggerState } from "react-stately";

import { cn } from "../utils";

interface PopoverProps extends Omit<AriaPopoverProps, "popoverRef"> {
	children: ReactNode;
	state: OverlayTriggerState;
	className?: string;
	popoverRef?: RefObject<HTMLDivElement>;
}

export function Popover(props: PopoverProps) {
	let ref = useRef<HTMLDivElement>(null);
	let { popoverRef = ref, state, children, className, isNonModal } = props;

	let { popoverProps, underlayProps } = usePopover({ ...props, popoverRef }, state);

	return (
		<Overlay>
			{!isNonModal && <div {...underlayProps} className="fixed inset-0" />}
			<div
				{...popoverProps}
				ref={popoverRef}
				className={cn(
					// IMPORTANT: Prevent Radix from disabling this overlay. Radix is setting
					// `pointer-events: none` on the document, and since this is not Radix, this
					// helpfully inherits that. Hooray!
					"pointer-events-auto",
					"p-1",
					"mt-2",
					"relative",
					"z-popover",
					"max-h-96",
					"min-w-[8rem]",
					"overflow-hidden",
					"rounded-md",
					"border",
					"bg-primary-raised",
					"shadow-md",
					"data-[state=open]:animate-in",
					"data-[state=closed]:animate-out",
					"data-[state=closed]:fade-out-0",
					"data-[state=open]:fade-in-0",
					"data-[state=closed]:zoom-out-95",
					"data-[state=open]:zoom-in-95",
					"data-[side=bottom]:slide-in-from-top-2",
					"data-[side=left]:slide-in-from-right-2",
					"data-[side=right]:slide-in-from-left-2",
					"data-[side=top]:slide-in-from-bottom-2",
					className
				)}
			>
				{!isNonModal && <DismissButton onDismiss={state.close} />}
				{children}
				<DismissButton onDismiss={state.close} />
			</div>
		</Overlay>
	);
}
